/**
 * Created by Simon Zhao on 06/27/2022.
 * The models associate with relationship automation dialog.
 * ------ maintenance history ------
 */

import { KeyValue } from '@angular/common';
import { FieldConfig } from '../../../../components/work-flow/field/field.model';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { AutomationRelationshipItem, AutomationRelationshipModel, VAR_PROJECT_ORIGINATOR } from '../../../../tamalelibs/models/automation.model';
import { EntityBrief } from '../../../../tamalelibs/models/entity-brief.model';
import { RelationshipType } from '../../../../tamalelibs/models/relationship-type.model';
import { ControlType } from '../../../../tamalelibs/models/template-control.model';
import { MultiSelDropdownConfig, MultiSelDropdownFilterConfig } from '../../../multi-sel-dropdown-filter/multi-sel-dropdown-filter.model';
export const ID_DELIMITER = ',';
export const ENTITY_SOURCE_TYPES = [{ value: 'field', text: 'Field' }, { value: 'entity', text: 'Entity' }];
export class DiagramRelationshipDialogOptions {
    existingRelations?: AutomationRelationshipModel;
    isUpdate: boolean;
    nodeId: string;
    taskArray: Array<KeyValue<string, string>>;
}

export class DiagramRelationshipDialogResult {
    isConfirmed: boolean;
    relationResult: AutomationRelationshipModel;
    nodeId: string;
    isUpdate: boolean;
}

export class SingleSelectionViewModel<T> {
    rawDataSource: Array<T> = [];
    filteredDataSource: Array<T> = [];
    placeholder: string;
    selectedValue: T;
}

export class MultipleEntitySelectionViewModel {
    filteredDataSource: Array<EntityBrief> = [];
    selectedEntityItems: Array<EntityBrief> = [];
    isLoading: boolean;
}

export class EntityFilterArgument {
    keyword: string;
    entityDataSource: MultipleEntitySelectionViewModel;
    dropdownRef: any;
}

export class AutomationRelationshipItemViewModel {
    /**
     * A collection of ED fields.
     */
    parentDataSource = new SingleSelectionViewModel<KeyValue<string, string>>();

    /**
     * A collection of relationship types.
     */
    relationshipTypeDataSource = new SingleSelectionViewModel<KeyValue<string, string>>();

    childFieldDataSource = new MultiSelDropdownFilterConfig();

    childEntityDataSource = new MultipleEntitySelectionViewModel();

    selectedChildEntitySrc: { value: 'field' | 'entity', text: string } = { value: 'field', text: '' };

    selectedChildEntityOrFieldIds: string;

    isAppend = true;

    static toRelationshipModel(srcVM: Array<AutomationRelationshipItemViewModel>): AutomationRelationshipModel {
        const relationModel = new AutomationRelationshipModel();
        relationModel.action = 'create';
        relationModel.objectType = 'relationship';
        srcVM.forEach(relationVM => {
            const relationItem = new AutomationRelationshipItem();
            relationItem.append = relationVM.isAppend;
            relationItem.parent = { type: 'field', ids: relationVM.parentDataSource.selectedValue.value };
            relationItem.type = relationVM.relationshipTypeDataSource.selectedValue.value;
            relationItem.child = { type: relationVM.selectedChildEntitySrc.value, ids: relationVM.selectedChildEntityOrFieldIds };
            relationModel.relationships.push(relationItem);
        });
        return relationModel;
    }

    /**
     * Creates a new instance of relation automation view model based on the given context.
     * @param controls a collection of field controls in current workflow.
     * @param relationTypes a relationship type collection.
     * @param preloadedEntities a collection of entity info, each one is associated with the given relationship config.
     * @param existingRelation the given relationship config.
     * @returns a instance of automation relationship view model, created based on given context.
     */
    static createNewRelationItem(controls: Array<FieldConfig>, relationTypes: Array<RelationshipType>, preloadedEntities: Array<EntityBrief>, taskOwnerList: Array<MultiSelDropdownConfig>, existingRelation?: AutomationRelationshipItem): AutomationRelationshipItemViewModel {
        const rowVM = new AutomationRelationshipItemViewModel();
        if (controls && controls.length > 0 && relationTypes && relationTypes.length > 0) {
            const childIdArray = [], parentIdArray = [], relationTypeIdArray = [];
            if (existingRelation) {
                // load up from the existing relationship config.
                if (existingRelation.child && existingRelation.child.ids) {
                    childIdArray.push(...existingRelation.child.ids.split(ID_DELIMITER));
                    rowVM.selectedChildEntityOrFieldIds = existingRelation.child.ids;
                }
                if (existingRelation.parent && existingRelation.parent.ids) {
                    parentIdArray.push(...existingRelation.parent.ids.split(ID_DELIMITER));
                }
                if (existingRelation.type) {
                    relationTypeIdArray.push(existingRelation.type);
                }
                rowVM.isAppend = existingRelation.append;
                rowVM.selectedChildEntitySrc = { value: existingRelation.child.type, text: '' };
                existingRelation.child.ids.split(ID_DELIMITER).forEach(
                    entityId => {
                        const matchEntity = preloadedEntities.find(e => e.id === entityId);
                        if (matchEntity) {
                            rowVM.childEntityDataSource.selectedEntityItems.push(matchEntity);
                        }
                    }
                );
            }
            controls.filter(ctrl => ctrl.type === ControlType.SINGLE_ENTITY_DROP_DOWN || ctrl.type === ControlType.MULTI_ENTITY_DROP_DOWN || ctrl.type === ControlType.SINGLE_USER_DROP_DOWN).forEach(fldCtrl => {
                const filterItem = new MultiSelDropdownConfig();
                filterItem.id = fldCtrl.fieldDefinitionId;
                filterItem.name = fldCtrl.label;
                filterItem.checked = childIdArray.includes(fldCtrl.fieldDefinitionId);
                rowVM.childFieldDataSource.data.push(filterItem);
            });
            rowVM.childFieldDataSource.data.push({ id: VAR_PROJECT_ORIGINATOR.value, name: VAR_PROJECT_ORIGINATOR.key, checked: childIdArray.includes(VAR_PROJECT_ORIGINATOR.value) });
            taskOwnerList.filter(to => childIdArray.includes(to.id)).forEach(toItem => toItem.checked = true);
            rowVM.childFieldDataSource.data.push(...taskOwnerList);
            rowVM.childFieldDataSource.placeholder = StringLiteralsPipe.translate('general.child_entity_field');

            rowVM.parentDataSource.placeholder = StringLiteralsPipe.translate('workflow.parent_entity_field');
            controls.filter(ctrl => ctrl.type === ControlType.SINGLE_ENTITY_DROP_DOWN || ctrl.type === ControlType.SINGLE_USER_DROP_DOWN).forEach(fldCtrl => {
                const filterItem = { key: fldCtrl.label, value: fldCtrl.fieldDefinitionId };
                rowVM.parentDataSource.rawDataSource.push(filterItem);
                rowVM.parentDataSource.filteredDataSource.push(filterItem);
                if (parentIdArray.includes(filterItem.value)) {
                    rowVM.parentDataSource.selectedValue = { key: fldCtrl.label, value: fldCtrl.fieldDefinitionId };
                }
            });
            rowVM.relationshipTypeDataSource.placeholder = StringLiteralsPipe.translate('general.relationship_type');
            relationTypes.forEach(rType => {
                const typeItem = { key: rType.name, value: rType.id };
                rowVM.relationshipTypeDataSource.rawDataSource.push(typeItem);
                rowVM.relationshipTypeDataSource.filteredDataSource.push(typeItem);
                if (relationTypeIdArray.includes(typeItem.value)) {
                    rowVM.relationshipTypeDataSource.selectedValue = { key: rType.name, value: rType.id };
                }
            });
        }
        return rowVM;
    }
}
